import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CreditCard, Plus, Pencil, Trash2, Check, DollarSign, Star } from "lucide-react";

const COLORS = ["primary", "emerald", "amber", "rose", "violet", "sky"];
const COLOR_CLASSES = {
  primary: "bg-primary/10 border-primary/30 text-primary",
  emerald: "bg-emerald-100 border-emerald-300 text-emerald-800",
  amber: "bg-amber-100 border-amber-300 text-amber-800",
  rose: "bg-rose-100 border-rose-300 text-rose-800",
  violet: "bg-violet-100 border-violet-300 text-violet-800",
  sky: "bg-sky-100 border-sky-300 text-sky-800",
};

const empty = { name: "", price_usd: 0, price_syp: 0, months: 1, features: [], feature_codes: [], color: "primary", active: true };

export default function Plans() {
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [feat, setFeat] = useState("");

  const load = async () => {
    try {
      const [i, c] = await Promise.all([api.get("/plans"), api.get("/plans/features/catalog")]);
      setItems(i.data); setCatalog(c.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setFeat(""); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty, ...p, feature_codes: p.feature_codes || [] }); setFeat(""); setOpen(true); };

  const toggleFeatureCode = (code) => {
    setForm(f => {
      const has = (f.feature_codes || []).includes(code);
      return { ...f, feature_codes: has ? f.feature_codes.filter(c => c !== code) : [...(f.feature_codes || []), code] };
    });
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("الاسم مطلوب");
    try {
      if (editing) await api.put(`/plans/${editing.id}`, form);
      else await api.post("/plans", form);
      toast.success("تم الحفظ"); setOpen(false); load();
    } catch(e) { toast.error(formatErr(e)); }
  };

  const del = async (id) => {
    if (!confirm("حذف الخطة؟")) return;
    try { await api.delete(`/plans/${id}`); toast.success("تم الحذف"); load(); }
    catch(e) { toast.error(formatErr(e)); }
  };

  const addFeat = () => {
    if (!feat.trim()) return;
    setForm(f => ({ ...f, features: [...(f.features || []), feat.trim()] })); setFeat("");
  };
  const rmFeat = (i) => setForm(f => ({ ...f, features: f.features.filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-4" data-testid="plans-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-primary grid place-items-center text-primary-foreground"><CreditCard className="w-5 h-5" /></div>
        <div className="flex-1">
          <h1 className="text-2xl md:text-3xl font-heading font-bold">خطط الاشتراك</h1>
          <p className="text-sm text-muted-foreground">حدّد الباقات التي تبيعها للشركات المشتركة</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="new-plan-btn"><Plus className="w-4 h-4" /> خطة جديدة</Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(p => (
          <Card key={p.id} className={`p-5 border-2 ${COLOR_CLASSES[p.color] || COLOR_CLASSES.primary} relative`} data-testid={`plan-${p.id}`}>
            {p.active && <Star className="w-4 h-4 absolute top-3 end-3 opacity-40" />}
            <div className="font-heading font-bold text-xl">{p.name}</div>
            <div className="mt-2 flex items-baseline gap-1">
              <span className="text-3xl font-bold">${p.price_usd}</span>
              <span className="text-sm opacity-70">/ {p.months === 1 ? "شهر" : `${p.months} أشهر`}</span>
            </div>
            {p.price_syp > 0 && <div className="text-xs opacity-70 mt-1">أو {p.price_syp.toLocaleString("ar-EG")} ل.س</div>}
            <ul className="mt-4 space-y-1.5">
              {(p.features || []).map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 shrink-0 mt-0.5" /><span>{f}</span></li>
              ))}
              {(!p.features || p.features.length === 0) && <li className="text-xs opacity-60">لا توجد ميزات محددة</li>}
            </ul>
            {(p.feature_codes || []).length > 0 && (
              <div className="mt-3 text-[10px] px-2 py-1 rounded bg-black/10 inline-block">
                🔓 {p.feature_codes.length} ميزة مفعّلة
              </div>
            )}
            <div className="flex gap-2 mt-4">
              <Button size="sm" variant="outline" onClick={()=>openEdit(p)} className="flex-1" data-testid={`edit-plan-${p.id}`}><Pencil className="w-3 h-3" /></Button>
              <Button size="sm" variant="outline" onClick={()=>del(p.id)} className="text-destructive flex-1" data-testid={`del-plan-${p.id}`}><Trash2 className="w-3 h-3" /></Button>
            </div>
          </Card>
        ))}
        {items.length === 0 && <div className="col-span-full text-center py-10 text-muted-foreground">لا توجد خطط بعد — ابدأ بإضافة خطة جديدة</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="plan-dialog">
          <DialogHeader><DialogTitle>{editing ? "تعديل الخطة" : "خطة جديدة"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>اسم الخطة</Label><Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="plan-name-input" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>السعر (USD)</Label><Input type="number" step="0.01" value={form.price_usd} onChange={(e)=>setForm({...form, price_usd:+e.target.value})} data-testid="plan-usd-input" /></div>
              <div><Label>السعر (SYP)</Label><Input type="number" value={form.price_syp} onChange={(e)=>setForm({...form, price_syp:+e.target.value})} data-testid="plan-syp-input" /></div>
            </div>
            <div><Label>المدة (بالأشهر)</Label><Input type="number" min="1" value={form.months} onChange={(e)=>setForm({...form, months:+e.target.value || 1})} data-testid="plan-months-input" /></div>
            <div>
              <Label>اللون</Label>
              <div className="flex gap-2 mt-1">
                {COLORS.map(c => (
                  <button key={c} onClick={()=>setForm({...form, color:c})}
                    className={`w-8 h-8 rounded-full border-2 ${form.color === c ? "ring-2 ring-offset-2 ring-primary" : "border-transparent"} ${COLOR_CLASSES[c]}`} title={c} />
                ))}
              </div>
            </div>
            <div>
              <Label>الميزات المفعّلة في هذه الباقة (تُطبَّق تلقائياً على الشركات المشتركة)</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 mt-1 border border-border rounded-md p-2 bg-muted/30 max-h-52 overflow-y-auto">
                {catalog.map(f => {
                  const on = (form.feature_codes || []).includes(f.code);
                  return (
                    <label key={f.code} className={`flex items-start gap-2 text-xs p-2 rounded cursor-pointer transition ${on ? "bg-primary/10 border border-primary/30" : "hover:bg-background border border-transparent"}`} data-testid={`feat-code-${f.code}`}>
                      <input type="checkbox" checked={on} onChange={()=>toggleFeatureCode(f.code)} className="mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold">{f.name}</div>
                        <div className="text-[10px] text-muted-foreground">{f.desc}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">✓ الأساسيات (POS, فواتير, تقارير أساسية...) متاحة في كل الباقات ولا تظهر هنا</div>
            </div>
            <div>
              <Label>ميزات إضافية معروضة على البطاقة (نص حر)</Label>
              <div className="flex gap-2 mt-1">
                <Input value={feat} onChange={(e)=>setFeat(e.target.value)}
                  onKeyDown={(e)=>{ if (e.key === "Enter") { e.preventDefault(); addFeat(); } }}
                  placeholder="مثال: طباعة حرارية" data-testid="plan-feature-input" />
                <Button type="button" onClick={addFeat} variant="outline">إضافة</Button>
              </div>
              <div className="mt-2 space-y-1">
                {(form.features || []).map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm bg-muted rounded px-2 py-1">
                    <Check className="w-3 h-3 shrink-0" /> <span className="flex-1">{f}</span>
                    <button onClick={()=>rmFeat(i)} className="text-destructive text-xs">×</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>إلغاء</Button>
            <Button onClick={save} data-testid="save-plan-btn">حفظ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
